import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ADJUSTMENT_DATE_KEY,
  ADJUSTMENT_KEY,
  BAD_DEBT_DATE_KEY,
  BAD_DEBT_KEY,
  COMPANY_NAME_KEY,
  CREATED_AT_KEY,
  DEAL_DUE_KEY,
  DEAL_KEY,
  DEAL_STAGE_KEY,
  ENGAGEMENT_MANAGER_KEY,
  NET_VALUE_KEY,
  NOTES_KEY,
  OTHER_CONSULTANT_KEY,
  PAY_1_DATE_KEY,
  PAY_2_DATE_KEY,
  PAY_3_DATE_KEY,
  PAYMENT_1_KEY,
  PAYMENT_2_KEY,
  PAYMENT_3_KEY,
  REFUND_DATE_KEY,
  REFUND_KEY,
  RENEWAL_RATE_KEY,
  SPLIT_KEY,
  STATUS_KEY,
  SUPPORTING_DUE_DATE_KEY,
  VALUATION_DATE_KEY,
  VALUATION_FEE_KEY,
} from 'common/constants/analyst-dashboards';
import { AttioQueryKeys } from 'dashboard409a/common/types/commonTypes';
import EditNoteDialog from 'dashboard409a/components/EditNoteDialog';
import { addBusinessDays } from 'dashboard409a/components/OperationsModal/useOperationsModal';
import { modelStatusMap } from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.constants';
import { DealStageState } from 'dashboard409a/pages/Operations/components/ValuationDealInfo/ValuationDealInfo.props';
import { ModelStatus as ModelStatusEnum } from 'dashboard409a/pages/Workboard/common/types';
import { updateValuationStatus } from 'dashboard409a/services/attio-common';
import { Note } from 'dashboard409a/services/models.types';
import { executeFulfillmentSideEffectsForStatusChange } from 'dashboard409a/utilities/fulfillmentOnStatusChange';
import { Logger } from 'utilities';
import { StyledSpanEmptyStatus, StyledStatusChip, StyledTableCell, StyledTableRow } from '../common';
import { StatusDropdown } from '../StatusDropdown';
import { ValuationsTableRowProps } from './ValuationsTableRow.types';
import { EnhancedTableHeadColumn } from '../ValuationsTable.types';
import CompanyNameCell from './cells/CompanyNameCell';
import ConsultantCell from './cells/ConsultantCell';
import DealCell from './cells/DealCell';
import DealDueCell from './cells/DealDueCell';
import DealStageCell from './cells/DealStageCell';
import EngagementManagerCell from './cells/EngagementManagerCell';
import FinancialFieldCell from './cells/FinancialFieldCell';
import NotesCell from './cells/NotesCell';

const ValuationsTableRow = ({
  valuationRow,
  onRowClick,
  onStatusUpdate,
  onNoteUpdate,
  onNoteSave,
  statusOptions,
  statusOptionsLoading,
  isNoteLoading = false,
  noteError = null,
  columns,
  columnWidths,
  onDealStageChange,
  dealStageOptions = [],
  onEngagementManagerUpdate,
}: ValuationsTableRowProps) => {
  const queryClient = useQueryClient();
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [dealStageState, setDealStageState] = useState<DealStageState>(DealStageState.IDLE);

  // Helper function to get cell width for a column
  const getCellWidth = (columnId: string, defaultWidth: string) => {
    if (columnWidths?.[columnId]) {
      return {
        width: `${columnWidths[columnId]}px`,
        minWidth: `${columnWidths[columnId]}px`,
      };
    }
    return { width: defaultWidth };
  };

  // Check if status is final and should be read-only
  const isFinalStatus = valuationRow.status === 'Final Sent';

  const handleStatusChange = async (newStatus: string, statusOptionId: string) => {
    try {
      setIsUpdatingStatus(true);

      // Update status via API
      await updateValuationStatus(valuationRow.record_id, statusOptionId);

      // Run fulfillment side effects (create/update) when status is Client Docs or Client Approval
      await executeFulfillmentSideEffectsForStatusChange(valuationRow.record_id, newStatus, {
        supportingDueDate: addBusinessDays(new Date(), 4),
        draftModelStatusId: modelStatusMap[ModelStatusEnum.DRAFT],
        queryClient,
      });

      // Update local data
      const updatedRow = { ...valuationRow, status: newStatus };

      // Notify parent component of the update
      if (onStatusUpdate) {
        onStatusUpdate(updatedRow);
      }

      // Invalidate the list and the individual detail cache so both the table
      // and the detail modal reflect the new status. Runs after the PATCH
      // completes — no race with stale Attio data.
      queryClient.invalidateQueries({ queryKey: [AttioQueryKeys.VALUATIONS_ALL] });
      queryClient.invalidateQueries({ queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationRow.record_id] });

      Logger.info('Status successfully updated to:', newStatus);
      setIsEditingStatus(false);
    } catch (error) {
      Logger.error('Error updating status:', error);
      // Could add toast notification here for better UX
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleNoteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsNoteDialogOpen(true);
  };

  const handleNoteSave = async (newNote: string): Promise<boolean> => {
    if (onNoteSave) {
      // Get the current note content
      const currentNoteContent = valuationRow.note?.content || '';
      await onNoteSave(newNote, currentNoteContent);
    } else if (onNoteUpdate) {
      // Fallback to old behavior
      const updatedRow = { ...valuationRow, note: { content: newNote } as Note };
      onNoteUpdate(updatedRow);
    }

    // Always close the dialog after saving
    return false;
  };

  const handleDealStageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDealStageState(DealStageState.EDITING);
  };

  const handleDealStageChange = async (value: { id: string; label: string } | null) => {
    if (!value || !onDealStageChange) {
      return;
    }

    setDealStageState(DealStageState.UPDATING);
    try {
      await onDealStageChange(value.id);
    } finally {
      setDealStageState(DealStageState.IDLE);
    }
  };

  // Financial fields configuration
  const financialFields = [
    { columnId: NET_VALUE_KEY, fieldKey: 'net_value' as const },
    { columnId: SPLIT_KEY, fieldKey: 'split' as const },
    { columnId: PAYMENT_1_KEY, fieldKey: 'payment_1' as const },
    { columnId: PAY_1_DATE_KEY, fieldKey: 'pay_1_date' as const },
    { columnId: PAYMENT_2_KEY, fieldKey: 'payment_2' as const },
    { columnId: PAY_2_DATE_KEY, fieldKey: 'pay_2_date' as const },
    { columnId: PAYMENT_3_KEY, fieldKey: 'payment_3' as const },
    { columnId: PAY_3_DATE_KEY, fieldKey: 'pay_3_date' as const },
    { columnId: REFUND_KEY, fieldKey: 'refund' as const },
    { columnId: REFUND_DATE_KEY, fieldKey: 'refund_date' as const },
    { columnId: ADJUSTMENT_KEY, fieldKey: 'adjustment' as const },
    { columnId: ADJUSTMENT_DATE_KEY, fieldKey: 'adjustment_date' as const },
    { columnId: BAD_DEBT_KEY, fieldKey: 'bad_debt' as const },
    { columnId: BAD_DEBT_DATE_KEY, fieldKey: 'bad_debt_date' as const },
    { columnId: RENEWAL_RATE_KEY, fieldKey: 'renewal_rate' as const },
  ];

  return (
    <StyledTableRow
      hover
      tabIndex={-1}
      key={valuationRow.record_id}
      style={{ background: '#ffff' }}
      onClick={event => onRowClick(valuationRow, event)}>
      <StyledTableCell
        align="left"
        sx={{
          position: 'sticky',
          left: 0,
          zIndex: 1,
          backgroundColor: '#ffff',
          boxShadow: '2px 0 4px rgba(0, 0, 0, 0.1)',
          width: columnWidths?.[COMPANY_NAME_KEY] ? `${columnWidths[COMPANY_NAME_KEY]}px` : '150px',
          minWidth: columnWidths?.[COMPANY_NAME_KEY] ? `${columnWidths[COMPANY_NAME_KEY]}px` : '150px',
          maxWidth: columnWidths?.[COMPANY_NAME_KEY] ? `${columnWidths[COMPANY_NAME_KEY]}px` : '150px',
        }}>
        <CompanyNameCell valuationRow={valuationRow} />
      </StyledTableCell>
      <StyledTableCell
        align="left"
        sx={{
          width: columnWidths?.[STATUS_KEY] ? `${columnWidths[STATUS_KEY]}px` : '100px',
          minWidth: columnWidths?.[STATUS_KEY] ? `${columnWidths[STATUS_KEY]}px` : '100px',
        }}>
        <StatusDropdown
          currentStatus={valuationRow.status}
          onStatusChange={handleStatusChange}
          isEditing={isEditingStatus}
          onEditEnd={() => setIsEditingStatus(false)}
          statusOptions={statusOptions}
          loading={statusOptionsLoading}
        />

        {!isEditingStatus && (
          <>
            {valuationRow.status ? (
              <StyledStatusChip
                label={isUpdatingStatus ? 'Updating...' : valuationRow.status}
                status={valuationRow.status}
                onClick={e => {
                  e.stopPropagation();
                  if (!isUpdatingStatus && !isFinalStatus) {
                    setIsEditingStatus(true);
                  }
                }}
                style={{
                  cursor: isUpdatingStatus || isFinalStatus ? 'not-allowed' : 'pointer',
                  opacity: isUpdatingStatus ? 0.6 : 1,
                }}
                title={isFinalStatus ? 'Final status cannot be changed' : undefined}
              />
            ) : (
              <StyledSpanEmptyStatus
                onClick={e => {
                  e.stopPropagation();
                  if (!isUpdatingStatus && !isFinalStatus) {
                    setIsEditingStatus(true);
                  }
                }}
                style={{
                  cursor: isUpdatingStatus || isFinalStatus ? 'not-allowed' : 'pointer',
                  color: '#666',
                  fontStyle: 'italic',
                  opacity: isUpdatingStatus ? 0.6 : 1,
                }}>
                {isUpdatingStatus ? 'Updating...' : 'Click to select status'}
              </StyledSpanEmptyStatus>
            )}
          </>
        )}
      </StyledTableCell>
      <StyledTableCell
        align="left"
        onClick={handleNoteClick}
        sx={{
          ...getCellWidth(NOTES_KEY, 'auto'),
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}>
        <NotesCell valuationRow={valuationRow} />
      </StyledTableCell>
      {columns.some((column: EnhancedTableHeadColumn) => column.id === OTHER_CONSULTANT_KEY) && (
        <StyledTableCell
          align="left"
          sx={getCellWidth(OTHER_CONSULTANT_KEY, 'auto')}
          onClick={e => e.stopPropagation()}>
          <ConsultantCell valuationRow={valuationRow} />
        </StyledTableCell>
      )}
      {columns.some((column: EnhancedTableHeadColumn) => column.id === ENGAGEMENT_MANAGER_KEY) && (
        <StyledTableCell
          align="left"
          sx={getCellWidth(ENGAGEMENT_MANAGER_KEY, 'auto')}
          onClick={e => e.stopPropagation()}>
          <EngagementManagerCell valuationRow={valuationRow} onEngagementManagerUpdate={onEngagementManagerUpdate} />
        </StyledTableCell>
      )}
      {columns.some((column: EnhancedTableHeadColumn) => column.id === VALUATION_DATE_KEY) && (
        <StyledTableCell sx={getCellWidth(VALUATION_DATE_KEY, '100px')} align="left">
          {valuationRow.valuation_date}
        </StyledTableCell>
      )}
      {columns.some((column: EnhancedTableHeadColumn) => column.id === CREATED_AT_KEY) && (
        <StyledTableCell sx={getCellWidth(CREATED_AT_KEY, '100px')} align="left">
          {valuationRow.created_at ? valuationRow.created_at.split('T')[0] : '-'}
        </StyledTableCell>
      )}
      {columns.some((column: EnhancedTableHeadColumn) => column.id === VALUATION_FEE_KEY) && (
        <StyledTableCell sx={getCellWidth(VALUATION_FEE_KEY, '100px')} align="left">
          {valuationRow.valuation_fee}
        </StyledTableCell>
      )}
      {columns.some((column: EnhancedTableHeadColumn) => column.id === SUPPORTING_DUE_DATE_KEY) && (
        <StyledTableCell sx={getCellWidth(SUPPORTING_DUE_DATE_KEY, '120px')} align="left">
          {valuationRow.supporting_due_date || '-'}
        </StyledTableCell>
      )}
      {columns.some((column: EnhancedTableHeadColumn) => column.id === DEAL_KEY) && (
        <StyledTableCell align="left" sx={getCellWidth(DEAL_KEY, 'auto')}>
          <DealCell valuationRow={valuationRow} />
        </StyledTableCell>
      )}
      {columns.some((column: EnhancedTableHeadColumn) => column.id === DEAL_DUE_KEY) && (
        <StyledTableCell align="left" sx={getCellWidth(DEAL_DUE_KEY, 'auto')}>
          <DealDueCell valuationRow={valuationRow} />
        </StyledTableCell>
      )}
      {columns.some((column: EnhancedTableHeadColumn) => column.id === DEAL_STAGE_KEY) && (
        <StyledTableCell align="left" sx={getCellWidth(DEAL_STAGE_KEY, 'auto')} onClick={e => e.stopPropagation()}>
          <DealStageCell
            valuationRow={valuationRow}
            dealStageOptions={dealStageOptions}
            dealStageState={dealStageState}
            onDealStageClick={handleDealStageClick}
            onDealStageChange={handleDealStageChange}
          />
        </StyledTableCell>
      )}
      {financialFields.map(({ columnId, fieldKey }) =>
        columns.some((column: EnhancedTableHeadColumn) => column.id === columnId) ? (
          <StyledTableCell key={columnId} align="left" sx={getCellWidth(columnId, 'auto')}>
            <FinancialFieldCell valuationRow={valuationRow} fieldKey={fieldKey} />
          </StyledTableCell>
        ) : null
      )}
      {/* Edit Note Dialog */}
      <EditNoteDialog
        open={isNoteDialogOpen}
        onClose={() => setIsNoteDialogOpen(false)}
        onSave={handleNoteSave}
        currentNote={valuationRow.note?.content || ''}
        companyName={valuationRow.company_name || ''}
        isLoading={isNoteLoading}
        error={noteError}
      />
    </StyledTableRow>
  );
};
export default ValuationsTableRow;
